import React, { useCallback, useEffect, useMemo } from 'react';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN } from 'utils';

import { SiteConfigManager, useApplicationState, useCasinoNewState } from '@atlas/core';
import { CASINO_NAVIGATION_TYPES, CasinoNavigationMenu } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';

import FilterSearch from './Filter/FilterSearch';
import { useCasinoFilterState } from './CasinoFilterContext';
import { casinoFilterTypeMap, deleteCasinoFilter, updateCasinoFilter } from './useCasinoFilter';

const buttonIconName = {
  all_games: 'games',
  favourites: 'casino-star',
  home: 'home',
  search: 'search',
};

const CasinoNavigation = ({
  activeItem,
  isSearchActive,
  setActiveItem,
  setIsSearchActive,
  setShowFavorites,
  showFavorites,
}) => {
  const { authenticated } = useApplicationState();
  const t = useTranslate();
  const { filter, updateFilter } = useCasinoFilterState();
  const {
    categories: { data: categories },
  } = useCasinoNewState();
  const navigationType =
    SiteConfigManager.getConfig('config.config.general.mobileCasinoNavigationView') ||
    CASINO_NAVIGATION_TYPES.NAVIGATION1;
  const buttonProperties = SiteConfigManager.getConfig('config.config.buttonProperties');

  const defaultNavigationButtons = useMemo(() => {
    const buttons = authenticated
      ? buttonProperties
      : buttonProperties.filter(button => button.type !== 'favourites');

    return buttons.map(button => {
      const imagePropertiesObj = { imageFetchPriority: 'height', imageLoading: 'eager' };
      const buttonIcon = button.useDefaultIcon
        ? { iconName: buttonIconName[button.type], ...imagePropertiesObj }
        : { iconUrl: imageUrlCDN(button.icon, CDN_TYPE_CMS), ...imagePropertiesObj };

      return {
        id: button.type,
        name: `${t(button.type)}`,
        ...buttonIcon,
      };
    });
  }, [authenticated, buttonProperties, t]);

  const items = useMemo(
    () => [
      ...defaultNavigationButtons,
      ...categories
        .sort((a, b) => a.position - b.position)
        .map(({ iconUrl, id, name }, index) => ({
          iconUrl: iconUrl ? encodeURI(imageUrlCDN(iconUrl, CDN_TYPE_GAME)) : null,
          id: id.toString(),
          name,
          ...(index < 3 && { imageFetchPriority: 'height', imageLoading: 'eager' }), // set properties for first 4 elements which are visible on screen
        })),
    ],
    [categories, defaultNavigationButtons],
  );

  useEffect(() => {
    if (!activeItem && categories.length) {
      if (filter.categories.length) {
        const defaultItem = items.find(({ id }) => filter.categories[0].toString() === id) || {
          id: 'home',
        };
        setActiveItem(defaultItem.id);
      } else {
        setActiveItem('home');
      }
    }
  }, [activeItem, items, filter, categories.length]);

  useEffect(() => {
    if (!isSearchActive) {
      if (filter.categories[0]) {
        setActiveItem(`${filter.categories[0]}`);
      } else if (!activeItem) {
        setActiveItem(showFavorites ? 'favourites' : 'home');
      }
    }
  }, [isSearchActive]);

  const onClickHandler = useCallback(item => {
    if (item === 'home') {
      setActiveItem('home');
      deleteCasinoFilter(casinoFilterTypeMap.providerNames);
    }
    if (item === 'search') {
      setIsSearchActive(true);
    } else {
      setActiveItem(item);
    }
  }, []);

  useEffect(() => {
    if (!activeItem) {
      return;
    }

    if (['search', 'home', 'all_games'].includes(activeItem)) {
      setShowFavorites(false);
      deleteCasinoFilter(casinoFilterTypeMap.category);
      return;
    }

    if (activeItem === 'favourites') {
      setShowFavorites(true);
      deleteCasinoFilter(casinoFilterTypeMap.category);
      return;
    }

    if (showFavorites) {
      setShowFavorites(false);
      updateFilter({ categories: [activeItem] });
    }

    updateCasinoFilter(casinoFilterTypeMap.category, activeItem);
  }, [activeItem]);

  return (
    <div className="casino-navigation">
      {isSearchActive ? (
        <FilterSearch isSearchActive={isSearchActive} setIsSearchActive={setIsSearchActive} />
      ) : (
        <CasinoNavigationMenu
          items={items}
          onClick={onClickHandler}
          selectedItem={activeItem}
          type={navigationType}
        />
      )}
    </div>
  );
};

export default CasinoNavigation;
